<template>
  <!-- Montado pelo App.vue, fora do cabeçalho, então não precisa de Teleport como o
       NovidadesModal — nenhum ancestral posicionado o prende embaixo do fundo escuro. -->
  <ModalShell
    ref="shell"
    class="atalhos-modal"
    id="helpModal"
    size="modal-xl"
    scrollable
    :static-backdrop="false"
    keyboard
    aria-labelledby="atalhos-titulo"
  >
    <template #header>
      <ModalHeader title="Atalhos do teclado" @close="handleCancel" />
    </template>

    <template #body>
      <div class="atalhos-busca">
        <label class="visually-hidden" for="atalhos-termo">Buscar atalho</label>
        <div class="atalhos-campo">
          <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
          <input
            id="atalhos-termo"
            ref="campoDeBuscaEl"
            v-model="termo"
            type="search"
            class="form-control"
            placeholder="Buscar por tecla ou ação — ex.: alt, ctrl+alt+e, follow"
            autocomplete="off"
          />
        </div>
        <!-- aria-live porque quem usa leitor de tela precisa ouvir a lista encolher -->
        <span class="atalhos-contagem" role="status" aria-live="polite">
          {{ textoDaContagem }}
        </span>
      </div>

      <p v-if="gruposVisiveis.length === 0" class="atalhos-vazio">
        Nenhum atalho encontrado para <strong>{{ termo.trim() }}</strong>.
      </p>

      <div v-else class="atalhos-grade">
        <section v-for="grupo in gruposVisiveis" :key="grupo.title" class="atalhos-grupo">
          <h2 class="atalhos-grupo-titulo">{{ grupo.title }}</h2>
          <p v-if="grupo.description" class="atalhos-grupo-descricao">
            {{ grupo.description }}
          </p>

          <dl class="atalhos-lista">
            <template v-for="atalho in grupo.atalhos" :key="`${grupo.title}-${atalho.keyCombo}`">
              <dt class="atalhos-teclas">
                <!-- <kbd> é o elemento do HTML para entrada de teclado, e cada tecla
                     vai separada para o olho não ter que decompor "Ctrl+Alt+E" -->
                <template v-for="(tecla, i) in dividirCombinacao(atalho.keyCombo)" :key="i">
                  <span v-if="i > 0" class="atalhos-mais" aria-hidden="true">+</span>
                  <kbd>{{ tecla }}</kbd>
                </template>
              </dt>
              <dd class="atalhos-descricao">{{ atalho.description }}</dd>
            </template>
          </dl>
        </section>
      </div>
    </template>
  </ModalShell>
</template>

<script setup>
import { computed, nextTick, ref, useTemplateRef } from 'vue'
import ModalShell from '@/components/common/modal/ModalShell.vue'
import ModalHeader from '@/components/common/modal/ModalHeader.vue'
import {
  agruparAtalhos,
  contarAtalhos,
  dividirCombinacao,
  textoDeContagem
} from '@/utils/atalhos.js'

const shell = useTemplateRef('shell')
const shortcuts = ref([])
const groups = ref([])
const termo = ref('')
const campoDeBuscaEl = ref(null)

const emit = defineEmits(['cancel', 'save', 'onLoading'])

const gruposVisiveis = computed(() => agruparAtalhos(shortcuts.value, groups.value, termo.value))

const totalDeAtalhos = computed(() => contarAtalhos(shortcuts.value, groups.value))

const encontrados = computed(() =>
  gruposVisiveis.value.reduce((total, grupo) => total + grupo.atalhos.length, 0)
)

const textoDaContagem = computed(() =>
  textoDeContagem(encontrados.value, totalDeAtalhos.value, termo.value)
)

// Os eventos do Bootstrap (`shown.bs.modal`) e a classe `show` vivem no elemento `.modal`,
// que agora é do ModalShell. Ele expõe só open/close, então o elemento vem pelo $el — a
// moldura tem raiz única por definição, é o próprio `.modal`.
const elementoDoModal = () => shell.value?.$el ?? null

// O campo de busca é o motivo de o modal existir, então o foco vai direto para ele. Tem
// que ser depois de `shown.bs.modal`: o Bootstrap foca o próprio modal ao terminar de
// abrir, e um focus antes disso seria desfeito.
const focarBusca = () => campoDeBuscaEl.value?.focus()

const openModal = async (options, groupOptions) => {
  shortcuts.value = options
  groups.value = groupOptions
  // Abre sempre limpo: a busca da consulta anterior esconderia atalhos sem explicação
  termo.value = ''

  await nextTick()
  elementoDoModal()?.addEventListener('shown.bs.modal', focarBusca, { once: true })

  shell.value?.open()
}

const closeModal = () => {
  shell.value?.close()
}

// Função para lidar com o cancelamento
const handleCancel = () => {
  closeModal()
  emit('cancel')
}

const estaAberto = () => elementoDoModal()?.classList.contains('show') ?? false

defineExpose({
  openModal,
  closeModal,
  estaAberto
})
</script>

<style scoped>
/* Os tokens semânticos do design system não têm variante de tema escuro — `--text-body` é
   sempre o mesmo. Quem vira o tema é a classe `dark` no body, que o app usa para trocar
   `--bs-modal-bg`. Então a paleta local continua existindo, só que agora cada valor sai de
   um token em vez de ser um hex escrito aqui. */
.atalhos-modal {
  --atalho-texto: var(--text-body);
  --atalho-texto-fraco: var(--text-subtle);
  --atalho-linha: var(--border-default);
  --atalho-tecla-fundo: var(--surface-sunken);
  --atalho-tecla-borda: var(--border-default);
  /* A linha de baixo da tecla precisa ser mais escura que a borda para dar relevo */
  --atalho-tecla-sombra: var(--text-muted);
}

/* O tema escuro da aplicação é a classe `dark` no body */
body.dark .atalhos-modal {
  --atalho-texto: var(--text-on-dark);
  --atalho-texto-fraco: var(--text-on-dark-muted);
  --atalho-linha: var(--border-inverse);
  --atalho-tecla-fundo: var(--tint-on-dark-strong);
  --atalho-tecla-borda: var(--border-inverse);
  --atalho-tecla-sombra: var(--surface-inverse);
}

/* --- Busca --- */
.atalhos-busca {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-5);
}

.atalhos-campo {
  position: relative;
  flex: 1 1 260px;
}

.atalhos-campo i {
  position: absolute;
  top: 50%;
  left: var(--space-4);
  transform: translateY(-50%);
  color: var(--atalho-texto-fraco);
  pointer-events: none;
}

.atalhos-campo .form-control {
  padding-left: var(--space-8);
}

.atalhos-contagem {
  color: var(--atalho-texto-fraco);
  font-size: var(--text-base);
  white-space: nowrap;
}

.atalhos-vazio {
  margin: 0;
  padding: var(--space-6) 0;
  color: var(--atalho-texto-fraco);
}

/* --- Grade de grupos ---
   O modal é xl e antes usava um terço da largura, com todos os grupos empilhados numa
   coluna só. Aqui cada grupo é um cartão e as colunas acompanham a largura disponível. */
.atalhos-grade {
  display: grid;
  /* 260px é o ponto em que uma coluna ainda cabe "Ctrl + Alt + F" mais a ação ao lado */
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--space-6);
}

.key {
  font-family: Consolas;
  font-size: 0.85em;
  padding: 4px;
  background-color: var(--surface-field-muted);
  margin-right: 5px;
}

.atalhos-grupo-titulo {
  margin: 0;
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-label);
  text-transform: uppercase;
  color: var(--atalho-texto-fraco);
}

.atalhos-grupo-descricao {
  margin: var(--space-1) 0 var(--space-4);
  font-size: var(--text-base);
  color: var(--atalho-texto-fraco);
}

/* --- Lista de atalhos ---
   dt/dd em duas colunas: as teclas alinhadas à esquerda e a ação ao lado, para o olho
   varrer a coluna de teclas de cima a baixo. */
.atalhos-lista {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: var(--space-3);
  margin: 0;
}

.atalhos-teclas {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  font-weight: var(--weight-regular);
  white-space: nowrap;
}

.atalhos-descricao {
  margin: 0;
  color: var(--atalho-texto);
  font-size: var(--text-md);
}

.atalhos-mais {
  color: var(--atalho-texto-fraco);
  font-size: var(--text-xs);
}

kbd {
  display: inline-block;
  min-width: 1.75em;
  padding: var(--space-1) var(--space-2);
  border: var(--border-width) solid var(--atalho-tecla-borda);
  border-radius: var(--radius-md);
  /* A linha de baixo mais grossa é o que dá o relevo de tecla */
  box-shadow: inset 0 -2px 0 var(--atalho-tecla-sombra);
  background: var(--atalho-tecla-fundo);
  color: var(--atalho-texto);
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  line-height: var(--leading-snug);
  text-align: center;
}

/* No celular o modal ocupa a tela toda e a coluna de teclas fica acima da ação */
@media (max-width: 575.98px) {
  .atalhos-lista {
    grid-template-columns: 1fr;
    gap: var(--space-1) 0;
  }

  .atalhos-descricao {
    margin-bottom: var(--space-3);
  }
}
</style>
